import { createRouter, createWebHistory } from "vue-router";
import store from "../store";

// Auth Views
import Login from "../views/Login.vue";
import SignUp from "../views/SignUp.vue";
import ForgotPassword from "../views/ForgotPassword.vue";
import ResetPassword from "../views/ResetPassword.vue";
import AwaitingApproval from "../views/AwaitingApproval.vue";
import VerifyEmail from "@/views/VerifyEmail.vue";

// Main Views
import DashboardView from "../views/DashboardView.vue";
import LearningGoalsView from "../views/LearningGoalsView.vue";
import LearningGoalsAll from "../views/LearningGoalsAll.vue";
import LearningGoalDetails from "../views/LearningGoalDetails.vue";
import LearningGoalTaskDetailComponent from "../components/learning-goals/LearningGoalTaskDetailComponent.vue";
import CreateLearningGoal from "../views/CreateLearningGoal.vue";
import MyHubsView from "../views/MyHubsView.vue";
import HubsListingView from "../views/HubsListingView.vue";
import JobsView from "../views/JobsView.vue";
import JobDetailsView from "../views/JobDetailsView.vue";
import FeedsView from "../views/FeedsView.vue";
import PricingView from "../views/PricingView.vue";
import CheckoutView from "../views/CheckoutView.vue";
import AIAssessment from "../views/AIAssessment.vue";
import Settings from "@/views/Settings.vue";
import Profile from "../views/Profile.vue";
import Credits from "../views/Credits.vue";
import LockInMode from "@/views/LockInMode.vue";

const routes = [
    {
        path: "/",
        redirect: "/login",
    },
    {
        path: "/login",
        name: "Login",
        component: Login,
        meta: { requiresAuth: false },
    },
    {
        path: "/signup",
        name: "SignUp",
        component: SignUp,
        meta: { requiresAuth: false },
    },
    {
        path: "/forgot-password",
        name: "ForgotPassword",
        component: ForgotPassword,
        meta: { requiresAuth: false },
    },
    {
        path: "/reset-password/:token",
        name: "ResetPassword",
        component: ResetPassword,
        meta: { requiresAuth: false },
    },
    { 
        path: "/verify-email/:email", 
        name: "VerifyEmail", 
        component: VerifyEmail,
        meta: { requiresAuth: false },
    },
    {
        path: "/awaiting-approval",
        name: "AwaitingApproval",
        component: AwaitingApproval,
        meta: { requiresAuth: true },
    },
    {
        path: "/home",
        name: "Home",
        component: DashboardView,
        meta: { requiresAuth: true },
        alias: "/home",
    },

    {
        path: "/learning-goals",
        name: "LearningGoals",
        component: LearningGoalsView,
        meta: { requiresAuth: true },
    },
    {
        path: "/learning-goals/all",
        name: "LearningGoalsAll",
        component: LearningGoalsAll,
        meta: { requiresAuth: true },
    },
    {
        path: "/learning-goals/create",
        name: "CreateLearningGoal",
        component: CreateLearningGoal,
        meta: { requiresAuth: true },
    },
    {
        path: "/learning-goals/:id",
        name: "LearningGoalDetails",
        component: LearningGoalDetails,
        meta: { requiresAuth: true },
    },
    {
        path: "/learning-goals/:projectId/ai-assessment",
        name: "AIAssessment",
        component: AIAssessment,
        meta: { requiresAuth: true },
    },
    {
        path: "/learning-goals/:id/tasks/:taskId",
        name: "LearningGoalTaskDetails",
        component: LearningGoalTaskDetailComponent,
        meta: { requiresAuth: true },
    },
    {
        path: "/learning-goals/:id/lock-in/",
        name: "LockInMode",
        component: LockInMode,
        meta: { requiresAuth: true }
    },
    {
        path: "/my-hubs",
        name: "MyHubs",
        component: HubsListingView,
        meta: { requiresAuth: true },
    },
    {
        path: "/my-hubs/:id",
        name: "MyHubDetails",
        component: MyHubsView,
        meta: { requiresAuth: true },
    },
    {
        path: "/feed",
        name: "Feed",
        component: FeedsView,
        meta: { requiresAuth: true },
    },
    {
        path: "/jobs",
        name: "Jobs",
        component: JobsView,
        meta: { requiresAuth: true },
    },
    {
        path: "/jobs/:id",
        name: "JobDetails",
        component: JobDetailsView,
        meta: { requiresAuth: true },
    },
    {
        path: "/pricing",
        name: "Pricing",
        component: PricingView,
        meta: { requiresAuth: true },
    },
    {
        path: "/checkout",
        name: "Checkout",
        component: CheckoutView,
        meta: { requiresAuth: true },
    },
    {
        path: "/notifications",
        name: "Notifications",
        redirect: "/home",
        meta: { requiresAuth: true },
    },
    {
        path: "/messages",
        name: "Messages",
        redirect: "/home",
        meta: { requiresAuth: true },
    },
    {
        path: "/profile",
        name: "Profile",
        component: Profile,
        meta: { requiresAuth: true },
    },
    {
        path: "/settings",
        name: "Settings",
        component: Settings,
        meta: { requiresAuth: true },
    },
    {
        path: "/credits",
        name: "Credits",
        component: Credits,
        meta: { requiresAuth: true },
    },
    {
        path: "/mentorship",
        name: "Mentorship",
        component: () => import("../views/MyMentorshipView.vue"),
        meta: { requiresAuth: true },
    },
    // Catch all 404
    {
        path: "/:pathMatch(.*)*",
        redirect: "/home",
    },
];

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        } else {
            return { top: 0 };
        }
    },
});

// Navigation Guard
router.beforeEach((to, from, next) => {
    const tokenFromStorage = localStorage.getItem("token");
    const tokenFromStore = store.state.token;
    const userFromStore = store.state.user;

    const isAuthenticated =
        (tokenFromStorage && tokenFromStore && userFromStore) ||
        (tokenFromStorage && userFromStore);
    const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);

    if (requiresAuth && !isAuthenticated) {
        // Redirect to login if not authenticated
        next({ name: "Login" });
    } else if (!requiresAuth && isAuthenticated && to.name === "Login") {
        // Redirect to home if already authenticated and trying to access login
        next({ name: "Home" });
    } else {
        next();
    }
});

export default router;
